<template>
  <section class="billing-history" aria-labelledby="billing-history-title">
    <div class="billing-history__heading">
      <div>
        <span class="billing-eyebrow">Histórico</span>
        <h2 id="billing-history-title">Pagamentos anteriores</h2>
        <p>{{ paginationSummary }}</p>
      </div>
    </div>

    <div v-if="loading" class="billing-history__skeletons" aria-label="Carregando histórico de pagamentos">
      <div v-for="item in 4" :key="item" class="billing-history__skeleton">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>

    <div v-else-if="getErrorPaymentHistory" class="billing-feedback billing-feedback--error" role="alert">
      <div>
        <i class="ph ph-warning-circle" aria-hidden="true"></i>
        <span>{{ getErrorPaymentHistory }}</span>
      </div>
      <button type="button" @click="loadPage(currentPage)">Tentar novamente</button>
    </div>

    <template v-else-if="hasHistory">
      <div class="billing-history__table-wrap">
        <table class="billing-history__table">
          <thead>
            <tr>
              <th scope="col">Referência</th>
              <th scope="col">Status</th>
              <th scope="col">Pagamento</th>
              <th scope="col" class="text-end">Valor</th>
              <th scope="col" class="text-end">Link</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in getInvoiceHistory" :key="historyKey(item)">
              <td>
                <div class="billing-history__reference">
                  <span><i class="ph ph-calendar-check" aria-hidden="true"></i></span>
                  <strong>{{ referenceLabel(item) }}</strong>
                </div>
              </td>
              <td>
                <span class="billing-status" :class="statusClass(item.status)">
                  {{ statusLabel(item.status) }}
                </span>
              </td>
              <td>{{ item.date_payment_formatted || 'Não pago' }}</td>
              <td class="text-end"><strong>{{ money(item.value_formatted || item.value) }}</strong></td>
              <td class="text-end">
                <a
                  v-if="item.payment_url && item.status !== 'paid'"
                  :href="item.payment_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="billing-link-button"
                  title="Abrir link de pagamento"
                  aria-label="Abrir link de pagamento em uma nova aba"
                >
                  <i class="ph ph-arrow-square-out" aria-hidden="true"></i>
                </a>
                <span v-else class="billing-history__muted">{{ item.status === 'paid' ? 'Pagamento confirmado' : 'Indisponível' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="billing-history__cards">
        <article v-for="item in getInvoiceHistory" :key="`mobile-${historyKey(item)}`" class="billing-history-card">
          <div class="billing-history-card__top">
            <span class="billing-history__reference-icon"><i class="ph ph-calendar-check" aria-hidden="true"></i></span>
            <div>
              <strong>{{ referenceLabel(item) }}</strong>
              <span>{{ item.date_payment_formatted || 'Não pago' }}</span>
            </div>
            <span class="billing-status" :class="statusClass(item.status)">
              {{ statusLabel(item.status) }}
            </span>
          </div>

          <div class="billing-history-card__bottom">
            <strong>{{ money(item.value_formatted || item.value) }}</strong>
            <a
              v-if="item.payment_url && item.status !== 'paid'"
              :href="item.payment_url"
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir fatura
              <i class="ph ph-arrow-right" aria-hidden="true"></i>
            </a>
            <span v-else>{{ item.status === 'paid' ? 'Pagamento confirmado' : 'Link indisponível' }}</span>
          </div>
        </article>
      </div>

      <div class="billing-pagination">
        <span>{{ paginationSummary }}</span>
        <div>
          <button type="button" :disabled="currentPage <= 1 || loading" @click="goToPage(currentPage - 1)">
            <i class="ph ph-caret-left" aria-hidden="true"></i>
            Anterior
          </button>
          <button type="button" :disabled="currentPage >= lastPage || loading" @click="goToPage(currentPage + 1)">
            Próxima
            <i class="ph ph-caret-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </template>

    <div v-else class="billing-empty">
      <span><i class="ph ph-receipt" aria-hidden="true"></i></span>
      <strong>Nenhum pagamento encontrado</strong>
      <p>Quando houver faturas, elas aparecerão aqui para consulta rápida.</p>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";

interface InvoiceHistoryItem {
  invoice_type?: string;
  id?: number | string;
  month?: number | string;
  year?: number | string;
  status?: string;
  date_payment_formatted?: string | null;
  payment_url?: string | null;
  value?: number | string | null;
  value_formatted?: string | null;
}

export default defineComponent({
  name: "PaymentHistory",
  data() {
    return {
      currentPage: 1,
      loading: false,
    };
  },
  computed: {
    ...mapGetters([
      "getInvoiceHistory",
      "getInvoiceHistoryPagination",
      "getErrorPaymentHistory",
    ]),
    hasHistory(): boolean {
      return Array.isArray(this.getInvoiceHistory) && this.getInvoiceHistory.length > 0;
    },
    lastPage(): number {
      return Number(this.getInvoiceHistoryPagination?.last_page || 1);
    },
    paginationSummary(): string {
      const total = Number(this.getInvoiceHistoryPagination?.total || (this.getInvoiceHistory?.length || 0));
      if (!total) return "Nenhuma fatura registrada";
      return `Página ${this.currentPage} de ${this.lastPage} • ${total} ${total === 1 ? 'pagamento' : 'pagamentos'}`;
    },
  },
  mounted() {
    this.currentPage = Number(this.getInvoiceHistoryPagination?.current_page || 1);
  },
  methods: {
    ...mapActions([
      "setInvoiceHistory",
    ]),
    async loadPage(page: number) {
      const safePage = Math.max(1, page || 1);
      this.loading = true;

      try {
        await this.setInvoiceHistory(safePage);
        this.currentPage = Number(this.getInvoiceHistoryPagination?.current_page || safePage);
      } finally {
        this.loading = false;
      }
    },
    goToPage(page: number) {
      if (page < 1 || page > this.lastPage || this.loading) {
        return;
      }

      this.loadPage(page);
    },
    historyKey(item: InvoiceHistoryItem): string {
      return String(item.id || `${item.month || 'sem-mes'}-${item.year || 'sem-ano'}-${item.value || ''}`);
    },
    referenceLabel(item: InvoiceHistoryItem): string {
      if (!item.month || !item.year) return "Sem referência";
      return `${String(item.month).padStart(2, "0")}/${item.year}${item.invoice_type === "couple" ? " · Casal" : ""}`;
    },
    statusLabel(status?: string): string {
      const labels: Record<string, string> = {
        paid: "Paga",
        pending: "Pendente",
        overdue: "Vencida",
        canceled: "Cancelada",
      };

      return labels[String(status || "")] || status || "Indisponível";
    },
    statusClass(status?: string): string {
      if (status === "paid") return "is-paid";
      if (status === "overdue") return "is-overdue";
      if (status === "canceled") return "is-canceled";
      return "is-pending";
    },
    money(value?: number | string | null): string {
      if (value === undefined || value === null || value === "") return "R$ 0,00";
      const text = String(value);
      if (text.includes("R$")) return text;
      if (Number.isNaN(Number(text.replace(",", ".")))) return `R$ ${text}`;
      return Number(text.replace(",", ".")).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
  },
});
</script>

<style scoped>
.billing-history {
  background: var(--billing-surface, #ffffff);
  border: 1px solid var(--billing-border, #e9e9f0);
  border-radius: 22px;
  padding: 22px;
}

.billing-history__heading {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.billing-eyebrow {
  color: var(--billing-accent-dark, #05785d);
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.billing-history h2 {
  color: var(--billing-ink, #171629);
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.billing-history__heading p {
  color: var(--billing-muted, #747487);
  font-size: 12px;
  margin: 5px 0 0;
}

.billing-history__table-wrap {
  overflow-x: auto;
}

.billing-history__table {
  border-collapse: collapse;
  color: var(--billing-ink, #171629);
  min-width: 760px;
  width: 100%;
}

.billing-history__table th {
  color: var(--billing-muted, #747487);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 0 12px 12px;
  text-transform: uppercase;
  white-space: nowrap;
}

.billing-history__table td {
  border-top: 1px solid var(--billing-border, #e9e9f0);
  color: var(--billing-ink, #171629);
  font-size: 13px;
  font-weight: 600;
  padding: 14px 12px;
  vertical-align: middle;
}

.billing-history__table th:first-child,
.billing-history__table td:first-child {
  padding-left: 0;
}

.billing-history__table th:last-child,
.billing-history__table td:last-child {
  padding-right: 0;
}

.billing-history__reference {
  align-items: center;
  display: flex;
  gap: 10px;
}

.billing-history__reference span,
.billing-history__reference-icon {
  align-items: center;
  background: #e8f8f3;
  border-radius: 12px;
  color: var(--billing-accent-dark, #05785d);
  display: inline-flex;
  flex: 0 0 40px;
  font-size: 19px;
  height: 40px;
  justify-content: center;
  line-height: 1;
  width: 40px;
}

.billing-history__reference span i,
.billing-history__reference-icon i {
  display: block;
  line-height: 1;
}

.billing-status {
  border-radius: 999px;
  display: inline-flex;
  font-size: 10px;
  font-weight: 800;
  justify-content: center;
  line-height: 1;
  min-height: 26px;
  padding: 0 9px;
  text-align: center;
  white-space: nowrap;
  align-items: center;
}

.billing-status.is-paid {
  background: #e8f8f3;
  color: var(--billing-accent-dark, #05785d);
}

.billing-status.is-pending {
  background: #fff4df;
  color: #a76807;
}

.billing-status.is-overdue,
.billing-status.is-canceled {
  background: #fff0ee;
  color: var(--billing-danger, #c74f4a);
}

.billing-link-button {
  align-items: center;
  background: #e8f8f3;
  border: 1px solid #c9eee2;
  border-radius: 11px;
  color: var(--billing-accent-dark, #05785d);
  display: inline-flex;
  font-size: 18px;
  height: 38px;
  justify-content: center;
  text-decoration: none;
  width: 38px;
}

.billing-history__muted {
  color: var(--billing-muted, #747487);
  font-size: 12px;
}

.billing-history__cards {
  display: none;
}

.billing-history-card {
  border-bottom: 1px solid var(--billing-border, #e9e9f0);
  padding: 12px 0;
}

.billing-history-card:last-child {
  border-bottom: 0;
}

.billing-history-card__top {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 40px minmax(0, 1fr) auto;
}

.billing-history-card__top > div strong,
.billing-history-card__top > div span {
  display: block;
}

.billing-history-card__top strong {
  color: var(--billing-ink, #171629);
  line-height: 1.2;
  font-size: 13px;
  font-weight: 800;
}

.billing-history-card__top div > span {
  color: var(--billing-muted, #747487);
  font-size: 11px;
  margin-top: 3px;
}

.billing-history-card__bottom {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
}

.billing-history-card__bottom strong {
  color: var(--billing-ink, #171629);
  font-size: 15px;
  font-weight: 800;
}

.billing-history-card__bottom a,
.billing-history-card__bottom span {
  align-items: center;
  color: var(--billing-accent-dark, #05785d);
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 4px;
  text-decoration: none;
}

.billing-history-card__bottom span {
  color: var(--billing-muted, #747487);
}

.billing-pagination {
  align-items: center;
  border-top: 1px solid var(--billing-border, #e9e9f0);
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 16px;
}

.billing-pagination > span {
  color: var(--billing-muted, #747487);
  font-size: 12px;
}

.billing-pagination > div {
  display: flex;
  gap: 8px;
}

.billing-pagination button,
.billing-feedback button {
  align-items: center;
  background: var(--billing-surface, #ffffff);
  border: 1px solid var(--billing-border, #e9e9f0);
  border-radius: 10px;
  color: var(--billing-muted, #747487);
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  gap: 4px;
  min-height: 36px;
  padding: 0 10px;
}

.billing-pagination button:disabled {
  opacity: 0.45;
}

.billing-feedback {
  align-items: center;
  border-radius: 14px;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  justify-content: space-between;
  padding: 12px 14px;
}

.billing-feedback > div {
  align-items: center;
  display: flex;
  gap: 8px;
}

.billing-feedback--error {
  background: #fff1f0;
  border: 1px solid #ffd5d1;
  color: #9c3732;
}

.billing-empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 42px 14px 30px;
  text-align: center;
}

.billing-empty > span {
  align-items: center;
  background: #e8f8f3;
  border-radius: 50%;
  color: var(--billing-accent-dark, #05785d);
  display: flex;
  font-size: 27px;
  height: 60px;
  justify-content: center;
  margin-bottom: 13px;
  width: 60px;
}

.billing-empty strong {
  color: var(--billing-ink, #171629);
  font-size: 16px;
  font-weight: 800;
}

.billing-empty p {
  color: var(--billing-muted, #747487);
  line-height: 1.45;
  margin: 6px 0 0;
}

.billing-history__skeletons {
  display: grid;
  gap: 10px;
}

.billing-history__skeleton {
  align-items: center;
  border-top: 1px solid var(--billing-border, #e9e9f0);
  display: grid;
  gap: 12px;
  grid-template-columns: 42px 1fr 90px;
  padding: 12px 0;
}

.billing-history__skeleton span {
  animation: billing-pulse 1.4s ease-in-out infinite;
  background: #e8e8ee;
  border-radius: 999px;
  height: 18px;
}

.billing-history__skeleton span:first-child {
  border-radius: 12px;
  height: 42px;
}

@media (max-width: 767px) {
  .billing-history {
    border-radius: 20px;
    padding: 18px 16px;
  }

  .billing-history h2 {
    font-size: 19px;
  }

  .billing-history__table-wrap {
    display: none;
  }

  .billing-history__cards {
    display: block;
  }

  .billing-pagination {
    display: block;
  }

  .billing-pagination > span {
    display: block;
    margin-bottom: 10px;
    text-align: center;
  }

  .billing-pagination > div {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .billing-pagination button {
    justify-content: center;
    min-height: 44px;
  }
}

body.dark .billing-history {
  --billing-ink: #f5f5f8;
  --billing-muted: #aaa9ba;
  --billing-surface: #29283b;
  --billing-border: #3b394e;
}

body.dark .billing-history .billing-history__reference span,
body.dark .billing-history .billing-history__reference-icon,
body.dark .billing-history .billing-status.is-paid,
body.dark .billing-history .billing-empty > span,
body.dark .billing-history .billing-link-button {
  background: rgba(6, 180, 138, 0.14);
  border-color: rgba(102, 224, 189, 0.22);
  color: #66e0bd;
}

body.dark .billing-history .billing-status.is-overdue,
body.dark .billing-history .billing-status.is-canceled,
body.dark .billing-history .billing-feedback--error {
  background: rgba(199, 79, 74, 0.17);
  border-color: rgba(255, 170, 164, 0.2);
  color: #ffaaa4;
}

body.dark .billing-history .billing-status.is-pending {
  background: rgba(242, 169, 59, 0.16);
  color: #f5bd63;
}

body.dark .billing-history .billing-history__skeleton span {
  background: #3a394d;
}

@keyframes billing-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}
</style>

<style>
body.dark .billing-history {
  --billing-ink: #f5f5f8;
  --billing-muted: #aaa9ba;
  --billing-surface: #29283b;
  --billing-border: #3b394e;
}

body.dark .billing-history .billing-history__reference span,
body.dark .billing-history .billing-history__reference-icon,
body.dark .billing-history .billing-status.is-paid,
body.dark .billing-history .billing-empty > span,
body.dark .billing-history .billing-link-button {
  background: rgba(6, 180, 138, 0.14);
  border-color: rgba(102, 224, 189, 0.22);
  color: #66e0bd;
}

body.dark .billing-history .billing-status.is-overdue,
body.dark .billing-history .billing-status.is-canceled,
body.dark .billing-history .billing-feedback--error {
  background: rgba(199, 79, 74, 0.17);
  border-color: rgba(255, 170, 164, 0.2);
  color: #ffaaa4;
}

body.dark .billing-history .billing-status.is-pending {
  background: rgba(242, 169, 59, 0.16);
  color: #f5bd63;
}

body.dark .billing-history .billing-history__skeleton span {
  background: #3a394d;
}
</style>
